import { useEffect, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import {
  Box,
  IconButton,
  Stack,
  Typography,
  Chip,
  CircularProgress,
  Tooltip,
  useTheme
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArchiveIcon from "@mui/icons-material/Archive";

import { useTesterIdentity } from "./TesterIdentityProvider";
import { closeQuestion } from "./api";

const QUESTION_STATUS_DISPLAY = {
  investigating: { label: "Investigating", color: "info" },
  answered: { label: "Answered", color: "success" },
  failed: { label: "Failed", color: "error" },
  "rate-limited": { label: "Rate-limited", color: "warning" },
  closed: { label: "Closed", color: "default" }
};

const getDisplay = (status) =>
  QUESTION_STATUS_DISPLAY[status] || {
    label: status || "unknown",
    color: "default"
  };

const QuestionView = ({ questionId, onBack }) => {
  const { palette } = useTheme();
  const { internalDb, getIdToken } = useTesterIdentity();
  const [question, setQuestion] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [closing, setClosing] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!internalDb || !questionId) {
      return undefined;
    }
    const unsubscribe = onSnapshot(
      doc(internalDb, "tester-questions", questionId),
      (snap) => {
        setQuestion(snap.exists() ? { id: snap.id, ...snap.data() } : null);
        setLoaded(true);
      },
      () => {
        setQuestion(null);
        setLoaded(true);
      }
    );
    return unsubscribe;
  }, [internalDb, questionId]);

  const handleClose = async () => {
    setClosing(true);
    setError(null);
    try {
      await closeQuestion({ questionId, getIdToken });
    } catch (err) {
      setError(err.message || "Failed to close");
    } finally {
      setClosing(false);
    }
  };

  if (!loaded) {
    return (
      <Box sx={{ p: 2, textAlign: "center" }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!question) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="textSecondary">
          Question not found.
        </Typography>
      </Box>
    );
  }

  const { label, color } = getDisplay(question.status);
  const isInvestigating = question.status === "investigating";

  return (
    <Stack
      sx={{ height: "100%", display: "flex", flexDirection: "column" }}
      spacing={0}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          p: 1,
          borderBottom: 1,
          borderColor: "divider"
        }}
      >
        <IconButton size="small" onClick={onBack}>
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" noWrap>
            {question.question}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
            <Chip label={label} size="small" color={color} />
          </Box>
        </Box>
        {question.status !== "closed" ? (
          <Tooltip title="Close question">
            <span>
              <IconButton size="small" onClick={handleClose} disabled={closing}>
                {closing ? (
                  <CircularProgress size={16} />
                ) : (
                  <ArchiveIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        ) : null}
      </Box>
      <Box sx={{ flex: 1, overflowY: "auto", p: 1.5 }}>
        <Stack spacing={2}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: palette.backgroundGray.light
            }}
          >
            <Typography variant="caption" color="textSecondary" gutterBottom>
              Your question
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
              {question.question}
            </Typography>
          </Box>
          {isInvestigating ? (
            <Box sx={{ p: 1.5, textAlign: "center" }}>
              <CircularProgress size={20} sx={{ mb: 1 }} />
              <Typography variant="body2" color="textSecondary">
                The agent is reading the code. Usually ~2–3 min.
              </Typography>
            </Box>
          ) : null}
          {question.status === "answered" && question.findings ? (
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                bgcolor: palette.background.paper,
                border: 1,
                borderColor: "divider"
              }}
            >
              <Typography variant="caption" color="textSecondary" gutterBottom>
                Findings
              </Typography>
              <Typography
                variant="body2"
                sx={{ whiteSpace: "pre-wrap" }}
                color="textPrimary"
              >
                {question.findings}
              </Typography>
            </Box>
          ) : null}
          {question.status === "failed" ? (
            <Box sx={{ p: 1.5 }}>
              <Typography variant="body2" color="error" gutterBottom>
                Investigation failed
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {question.error || "Unknown error. Try asking again."}
              </Typography>
            </Box>
          ) : null}
          {error ? (
            <Typography variant="caption" color="error">
              {error}
            </Typography>
          ) : null}
        </Stack>
      </Box>
    </Stack>
  );
};

export default QuestionView;
